import Header from '../components/Header';
import Footer from '../components/Footer';
import { Heart, AlertTriangle, Shield, HelpCircle } from 'lucide-react';

const ResponsibleGaming = () => {
  return (
    <div className="min-h-screen bg-regal-dark">
      <Header />
      <main className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                Responsible Gaming
              </h1>
              <p className="text-gray-400">Last updated: December 2, 2025</p>
            </div>
            
            <div className="prose prose-invert max-w-none">
              <div className="bg-regal-gray rounded-xl p-8 mb-8 border border-gray-800">
                <p className="text-xl text-gray-300 leading-relaxed">
                  At Regal Play, we are committed to promoting responsible gambling and player welfare. This page provides information, resources, and tools to help you gamble safely and maintain control over your gaming activities.
                </p>
              </div>

              <div className="space-y-8 text-gray-300 leading-relaxed">
                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Our Commitment</h2>
                  </div>
                  <p>
                    Regal Play is dedicated to ensuring that online gambling remains a form of entertainment rather than a source of harm. We recognize that while most people gamble responsibly, some individuals may develop gambling problems that require support and intervention.
                  </p>
                  <p>
                    We are committed to providing accurate information about responsible gambling, promoting awareness of problem gambling signs, and directing players to appropriate support resources. While we are not a gambling operator, we take our role in promoting responsible gambling seriously.
                  </p>
                  <p>
                    Our commitment extends to providing information about responsible gambling tools, self-exclusion programs, and support organizations. We encourage all players to gamble within their means and seek help if gambling becomes problematic.
                  </p>
                </section>

                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Recognizing Problem Gambling</h2>
                  </div>
                  <p>
                    Problem gambling can affect anyone, regardless of age, background, or income level. Recognizing warning signs early can help prevent gambling from becoming problematic. Common warning signs include:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-2 mt-4">
                    <li>Spending more time or money gambling than you can afford</li>
                    <li>Chasing losses by betting larger amounts to recover previous losses</li>
                    <li>Lying to family or friends about gambling activities or losses</li>
                    <li>Feeling restless or irritable when trying to reduce or stop gambling</li>
                    <li>Gambling to escape problems or relieve feelings of depression or anxiety</li>
                    <li>Neglecting work, school, family, or other responsibilities due to gambling</li>
                    <li>Borrowing money or selling possessions to fund gambling</li>
                    <li>Feeling guilt, anxiety, or depression related to gambling</li>
                    <li>Unsuccessful attempts to control, reduce, or stop gambling</li>
                  </ul>
                  <p>
                    If you recognize these signs in yourself or someone you know, it may indicate a gambling problem. Early recognition and intervention can prevent problems from escalating and help individuals regain control over their gambling behavior.
                  </p>
                  <p>
                    Remember that problem gambling is a recognized condition that can be treated. Seeking help is a sign of strength, not weakness. Many people successfully overcome gambling problems with appropriate support and treatment.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Setting Limits</h2>
                  <p>
                    Setting limits on your gambling activities is one of the most effective ways to maintain control. Most UK-licensed casinos offer tools that allow you to set deposit limits, loss limits, time limits, and wagering limits.
                  </p>
                  <p>
                    <strong className="text-white">Deposit Limits:</strong> Set maximum amounts you can deposit daily, weekly, or monthly. These limits cannot be increased immediately, giving you time to reconsider decisions made during emotional moments.
                  </p>
                  <p>
                    <strong className="text-white">Loss Limits:</strong> Cap the maximum amount you can lose over specific periods. These limits prevent extended losing streaks from depleting your bankroll beyond what you can afford.
                  </p>
                  <p>
                    <strong className="text-white">Time Limits:</strong> Restrict how long you can play during sessions. These limits help prevent extended gambling sessions where you might lose track of time and money.
                  </p>
                  <p>
                    <strong className="text-white">Reality Checks:</strong> Receive reminders about how long you've been playing at regular intervals. These reminders help maintain awareness of session duration and spending.
                  </p>
                  <p>
                    We encourage you to use these tools proactively, setting limits before problems develop rather than waiting until gambling becomes problematic. Setting limits demonstrates responsible gambling behavior and helps protect your financial wellbeing.
                  </p>
                </section>

                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Self-Exclusion</h2>
                  </div>
                  <p>
                    Self-exclusion allows you to block access to gambling sites for predetermined periods, from 24 hours to permanent closure. During exclusion periods, casinos must prevent all access attempts, including password resets or new account creation.
                  </p>
                  <p>
                    <strong className="text-white">Individual Casino Self-Exclusion:</strong> You can exclude yourself from specific casino operators by contacting them directly. They will block your account and prevent you from creating new accounts during the exclusion period.
                  </p>
                  <p>
                    <strong className="text-white">GamStop:</strong> This free UK service allows you to self-exclude from all UK-licensed gambling sites simultaneously. When you register with GamStop, all UK-licensed operators must honor your exclusion for the chosen period.
                  </p>
                  <p>
                    Self-exclusion is a serious commitment that should be used when you need a break from gambling or when gambling has become problematic. Exclusion periods cannot be reversed, so choose the duration carefully. Self-exclusion is most effective when combined with support and treatment for underlying issues.
                  </p>
                  <p>
                    To register with GamStop, visit www.gamstop.co.uk. The service is free, confidential, and can exclude you for 6 months, 1 year, or 5 years from all UK-licensed gambling sites.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Support Resources</h2>
                  <p>
                    If you're struggling with problem gambling, numerous support resources are available to help you. These organizations provide free, confidential advice and support to individuals affected by gambling problems.
                  </p>
                  <div className="bg-regal-gray rounded-lg p-6 border border-gray-800 mt-4 space-y-4">
                    <div>
                      <p className="text-white font-semibold mb-2">BeGambleAware</p>
                      <p className="text-gray-300">Website: www.begambleaware.org</p>
                      <p className="text-gray-300">Helpline: 0808 8020 133 (free and confidential)</p>
                      <p className="text-gray-300">Provides free information, advice, and support for anyone affected by problem gambling.</p>
                    </div>
                    <div>
                      <p className="text-white font-semibold mb-2">GamCare</p>
                      <p className="text-gray-300">Website: www.gamcare.org.uk</p>
                      <p className="text-gray-300">Helpline: 0808 8020 133</p>
                      <p className="text-gray-300">Offers free support, advice, and counseling for problem gamblers and their families.</p>
                    </div>
                    <div>
                      <p className="text-white font-semibold mb-2">GamAnon</p>
                      <p className="text-gray-300">Website: www.gamanon.org.uk</p>
                      <p className="text-gray-300">Support for families and friends affected by someone else's gambling problem.</p>
                    </div>
                    <div>
                      <p className="text-white font-semibold mb-2">GamStop</p>
                      <p className="text-gray-300">Website: www.gamstop.co.uk</p>
                      <p className="text-gray-300">Free self-exclusion service for all UK-licensed gambling sites.</p>
                    </div>
                  </div>
                  <p className="mt-4">
                    These organizations offer various services including helplines, live chat, forums, and face-to-face counseling. They understand problem gambling and can provide non-judgmental support to help you regain control.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Tips for Responsible Gambling</h2>
                  <p>
                    Gambling should be entertainment, not a way to make money or escape problems. Following these tips can help you gamble responsibly and maintain control:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-2 mt-4">
                    <li>Never gamble with money you cannot afford to lose</li>
                    <li>Set a budget before you start gambling and stick to it</li>
                    <li>Never chase losses—accept that losing is part of gambling</li>
                    <li>Set time limits for gambling sessions</li>
                    <li>Take regular breaks from gambling</li>
                    <li>Don't gamble when you're upset, depressed, or under the influence of alcohol or drugs</li>
                    <li>Balance gambling with other activities and interests</li>
                    <li>Don't view gambling as a way to solve financial problems</li>
                    <li>Keep track of how much time and money you spend gambling</li>
                    <li>Seek help if gambling becomes problematic or causes distress</li>
                  </ul>
                  <p>
                    Remember that gambling outcomes are determined by chance, and the house always has an edge. No strategy can guarantee wins, and losing is more likely than winning. Approach gambling with realistic expectations and view any losses as the cost of entertainment.
                  </p>
                </section>

                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <HelpCircle className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Helping Someone with a Gambling Problem</h2>
                  </div>
                  <p>
                    If someone you know has a gambling problem, you can help by encouraging them to seek support and providing emotional support during recovery. However, you cannot force someone to stop gambling—they must make that decision themselves.
                  </p>
                  <p>
                    Approach the conversation with compassion and without judgment. Express concern about their wellbeing rather than criticizing their gambling behavior. Encourage them to seek professional help and offer to support them through the process.
                  </p>
                  <p>
                    Don't enable problem gambling by lending money or covering losses. Set boundaries to protect yourself and your finances. Seek support for yourself through organizations like GamAnon, which provide help for families and friends affected by someone else's gambling.
                  </p>
                  <p>
                    Remember that problem gambling often stems from underlying issues like depression, anxiety, or trauma. Recovery may require addressing these underlying causes, not just stopping gambling behavior. Professional treatment can help address both gambling problems and underlying issues.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Underage Gambling</h2>
                  <p>
                    Online gambling is strictly prohibited for individuals under 18 years of age in the United Kingdom. All UK-licensed casinos must verify players' ages and prevent underage access.
                  </p>
                  <p>
                    Parents and guardians should monitor children's internet usage and educate them about the risks of gambling. Use parental control software to block access to gambling websites, and discuss responsible attitudes toward gambling with children.
                  </p>
                  <p>
                    If you become aware of underage gambling, report it to the relevant casino operator immediately. They are legally obligated to prevent underage access and will take appropriate action.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Our Role</h2>
                  <p>
                    As an independent review and comparison website, Regal Play does not operate gambling services. We provide information about casinos, bonuses, and gambling-related topics to help players make informed decisions.
                  </p>
                  <p>
                    We are committed to promoting responsible gambling through information, resources, and links to support organizations. However, we are not responsible for monitoring or controlling individual gambling behavior, and we cannot provide professional treatment or counseling.
                  </p>
                  <p>
                    If you need help with problem gambling, we encourage you to contact the support organizations listed on this page or seek professional treatment. Gambling problems are treatable, and help is available.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Contact Us</h2>
                  <p>
                    If you have questions about responsible gambling or need information about support resources, please contact us:
                  </p>
                  <div className="bg-regal-gray rounded-lg p-6 border border-gray-800 mt-4">
                    <p className="text-white font-semibold mb-2">Regal Play</p>
                    <p className="text-gray-300">Email: info@regal-play.com</p>
                    <p className="text-gray-300">Phone: +44-0544-403-303</p>
                    <p className="text-gray-300">Address: London, United Kingdom</p>
                  </div>
                  <p className="mt-4">
                    Remember: Gambling should be fun and entertaining. If it stops being fun, it's time to stop. Seek help if you need it—support is available, and recovery is possible.
                  </p>
                </section>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResponsibleGaming;

